import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Tooltip } from 'antd';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';

// ── MiniBar ────────────────────────────────────────────────────────────────
// Tiny horizontal bar showing a value vs. a max. Used in tables to give a
// quick visual scale for a numeric column without dragging in a chart lib.

interface MiniBarProps {
  value: number;
  max: number;
  tone?: 'ink' | 'violet' | 'green' | 'amber' | 'red';
  width?: number | string;
  label?: string;
}

export const MiniBar = observer(function MiniBar({
  value,
  max,
  tone = 'ink',
  width = 100,
  label,
}: MiniBarProps) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const fg =
    tone === 'green' ? colors.green[600]
      : tone === 'amber' ? colors.amber[600]
      : tone === 'red' ? colors.red[600]
      : tone === 'violet' ? colors.violet[600]
      : colors.ink[600];

  const bar = (
    <div
      style={{
        position: 'relative',
        width,
        height: 6,
        background: isDark ? colors.slate[700] : colors.slate[100],
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          width: `${pct}%`,
          background: fg,
          borderRadius: 3,
        }}
      />
    </div>
  );

  return label ? <Tooltip title={label}><span>{bar}</span></Tooltip> : bar;
});

// ── StackedBar ─────────────────────────────────────────────────────────────
// Horizontal stacked bar with multiple segments. Used for context-overload
// breakdown (Critical / Warning / Healthy).

export interface Segment {
  value: number;
  color: string;
  label: string;
}

export const StackedBar = observer(function StackedBar({
  segments,
  width = 200,
  height = 10,
}: {
  segments: Segment[];
  width?: number;
  height?: number;
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  if (total === 0) {
    return (
      <div
        style={{
          width,
          height,
          background: isDark ? colors.slate[700] : colors.slate[100],
          borderRadius: height / 2,
        }}
      />
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        width,
        height,
        borderRadius: height / 2,
        overflow: 'hidden',
        background: isDark ? colors.slate[700] : colors.slate[100],
      }}
    >
      {segments.map((s, i) => {
        const pct = (s.value / total) * 100;
        if (pct === 0) return null;
        return (
          <Tooltip key={i} title={`${s.label}: ${s.value}`}>
            <div style={{ width: `${pct}%`, background: s.color, height: '100%' }} />
          </Tooltip>
        );
      })}
    </div>
  );
});

// ── LineSparkline ──────────────────────────────────────────────────────────
// Inline SVG sparkline. Pure render, no deps.

export const LineSparkline = observer(function LineSparkline({
  values,
  width = 180,
  height = 36,
  tone = 'ink',
}: {
  values: number[];
  width?: number;
  height?: number;
  tone?: 'ink' | 'violet' | 'green' | 'amber';
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  if (values.length === 0) return <span style={{ color: isDark ? colors.slate[500] : colors.slate[400], fontSize: 11 }}>—</span>;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = values.length > 1 ? width / (values.length - 1) : 0;
  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  const stroke =
    tone === 'green' ? colors.green[600]
      : tone === 'amber' ? colors.amber[600]
      : tone === 'violet' ? colors.violet[600]
      : colors.ink[600];

  const lastX = (values.length - 1) * stepX;
  const lastY = height - ((values[values.length - 1] - min) / range) * (height - 4) - 2;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke={stroke}
        strokeWidth={1.6}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={lastX} cy={lastY} r={2.4} fill={stroke} />
    </svg>
  );
});

// ── HBarChart ──────────────────────────────────────────────────────────────
// Vertical list of horizontal bars with a label and a value. Used for the
// router-accuracy daily breakdown and tool-overflow ranking.

export interface BarRow {
  label: string;
  value: number;
  tone?: 'ink' | 'violet' | 'green' | 'amber' | 'red';
  hint?: string;
}

export const HBarChart = observer(function HBarChart({
  rows,
  unit,
}: {
  rows: BarRow[];
  unit?: string;
}) {
  const max = Math.max(0, ...rows.map((r) => r.value));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map((r, i) => (
        <div key={`${r.label}-${i}`} style={{ display: 'grid', gridTemplateColumns: '160px 1fr 70px', gap: 10, alignItems: 'center' }}>
          <span
            style={{
              fontSize: 12,
              fontFamily: "'JetBrains Mono', monospace",
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={r.hint}
          >
            {r.label}
          </span>
          <MiniBar value={r.value} max={max} tone={r.tone} width="100%" />
          <span style={{ fontSize: 12, fontFamily: "'JetBrains Mono', monospace", textAlign: 'right' }}>
            {r.value.toLocaleString()}{unit ? ` ${unit}` : ''}
          </span>
        </div>
      ))}
    </div>
  );
});

// ── useWidth ───────────────────────────────────────────────────────────────
// Measured width of a container, so SVG charts draw at real pixels (text stays
// crisp — a viewBox stretch would squash the axis labels).

function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => setWidth(Math.floor(entries[0].contentRect.width)));
    ro.observe(el);
    setWidth(Math.floor(el.getBoundingClientRect().width));
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

// ── StackedColumns ─────────────────────────────────────────────────────────
// One column per period, stacked by series (e.g. spend per day by model).
// Hovering a column explains it: every series, the total and a footnote.

export interface ColumnDatum {
  key: string;
  /** Axis label; shown on every Nth column so labels never collide. */
  label: string;
  /** Tooltip heading. */
  title: string;
  values: Array<{ series: string; value: number }>;
  footnote?: string;
}

export const StackedColumns = observer(function StackedColumns({
  data,
  series,
  colorOf,
  height = 220,
  format,
}: {
  data: ColumnDatum[];
  series: string[];
  colorOf: (series: string) => string;
  height?: number;
  format: (v: number) => string;
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [ref, width] = useWidth<HTMLDivElement>();
  const left = 52;
  const bottom = 24;
  const top = 8;
  const plotH = height - bottom - top;
  const totals = data.map((d) => d.values.reduce((s, v) => s + v.value, 0));
  const max = Math.max(0, ...totals);
  const niceMax = max <= 0 ? 1 : niceCeil(max);
  const slot = data.length > 0 ? Math.max(1, (width - left - 4) / data.length) : 0;
  const barW = Math.max(1, Math.min(slot * 0.72, 38));
  const labelEvery = Math.max(1, Math.ceil(56 / Math.max(slot, 1)));
  const grid = isDark ? colors.slate[700] : colors.slate[100];
  const axis = isDark ? colors.slate[400] : colors.slate[500];

  return (
    <div ref={ref} style={{ width: '100%', position: 'relative' }}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label="Spend over time">
          {[0, 0.25, 0.5, 0.75, 1].map((f) => {
            const y = top + plotH * (1 - f);
            return (
              <g key={f}>
                <line x1={left} x2={width} y1={y} y2={y} stroke={grid} />
                <text x={left - 6} y={y + 4} fontSize={10} fill={axis} textAnchor="end">{format(niceMax * f)}</text>
              </g>
            );
          })}
          {data.map((d, i) => {
            const x = left + i * slot + (slot - barW) / 2;
            let y = top + plotH;
            const ordered = series.map((s) => d.values.find((v) => v.series === s)).filter((v): v is { series: string; value: number } => !!v && v.value > 0);
            const tip = (
              <div style={{ fontSize: 12, minWidth: 160 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{d.title}</div>
                {ordered.length === 0 && <div style={{ opacity: 0.8 }}>No priced calls</div>}
                {ordered.map((v) => (
                  <div key={v.series} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: colorOf(v.series), display: 'inline-block' }} />
                    <span style={{ flex: 1 }}>{v.series}</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{format(v.value)}</span>
                  </div>
                ))}
                {ordered.length > 1 && (
                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.25)', marginTop: 4, paddingTop: 4, display: 'flex' }}>
                    <span style={{ flex: 1 }}>Total</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{format(totals[i])}</span>
                  </div>
                )}
                {d.footnote && <div style={{ marginTop: 4, opacity: 0.8 }}>{d.footnote}</div>}
              </div>
            );
            return (
              <Tooltip key={d.key} title={tip} mouseEnterDelay={0.05}>
                <g>
                  <rect x={left + i * slot} y={top} width={slot} height={plotH} fill="transparent" />
                  {ordered.map((v) => {
                    const h = (plotH * v.value) / niceMax;
                    y -= h;
                    return <rect key={v.series} x={x} y={y} width={barW} height={Math.max(h, 0.5)} rx={2} fill={colorOf(v.series)} />;
                  })}
                  {i % labelEvery === 0 && (
                    <text x={x + barW / 2} y={height - 6} fontSize={10} fill={axis} textAnchor="middle">{d.label}</text>
                  )}
                </g>
              </Tooltip>
            );
          })}
        </svg>
      )}
    </div>
  );
});

/** Round an axis maximum up to 1, 2, 2.5 or 5 × a power of ten. */
function niceCeil(v: number): number {
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}

// ── BandHistogram ──────────────────────────────────────────────────────────
// Counts per band with the count printed above each bar. Bands at or above
// `alertFrom` draw in the warning tone (the tail worth looking at).

export const BandHistogram = observer(function BandHistogram({
  bands,
  alertFrom,
  height = 210,
}: {
  bands: Array<{ label: string; count: number; hint?: string }>;
  alertFrom: number;
  height?: number;
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [ref, width] = useWidth<HTMLDivElement>();
  const bottom = 26;
  const top = 18;
  const plotH = height - bottom - top;
  const max = Math.max(1, ...bands.map((b) => b.count));
  const slot = bands.length > 0 ? width / bands.length : 0;
  const barW = Math.min(slot * 0.7, 64);
  const axis = isDark ? colors.slate[400] : colors.slate[500];
  const ink = isDark ? colors.slate[200] : colors.slate[700];

  return (
    <div ref={ref} style={{ width: '100%' }}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label="Runs per cost band">
          {bands.map((b, i) => {
            const h = (plotH * b.count) / max;
            const x = i * slot + (slot - barW) / 2;
            const fill = i >= alertFrom ? colors.red[500] : colors.ink[500];
            return (
              <Tooltip key={b.label} title={b.hint ?? `${b.count.toLocaleString()} runs · ${b.label}`}>
                <g>
                  <rect x={x} y={top + plotH - h} width={barW} height={Math.max(h, b.count > 0 ? 2 : 0)} rx={4} fill={fill} opacity={0.85} />
                  <text x={x + barW / 2} y={top + plotH - h - 5} fontSize={11} fontWeight={600} fill={ink} textAnchor="middle">
                    {b.count.toLocaleString()}
                  </text>
                  <text x={x + barW / 2} y={height - 8} fontSize={10.5} fill={axis} textAnchor="middle">{b.label}</text>
                </g>
              </Tooltip>
            );
          })}
        </svg>
      )}
    </div>
  );
});
